import type { RegaValidateResponse, RegaDisplayData, RegaLocation } from "@/types/rega";

/**
 * Maps REGA property type strings (Arabic/English) to Dallali property_type enum.
 */
export function mapRegaToPropertyType(regaType: string): string {
  if (!regaType || typeof regaType !== 'string') return "apartment";
  const lower = regaType.toLowerCase();
  if (lower.includes("أرض") || lower.includes("land")) return "land";
  if (lower.includes("فيلا") || lower.includes("villa")) return "villa";
  if (lower.includes("شقة") || lower.includes("apartment")) return "apartment";
  if (lower.includes("استوديو") || lower.includes("studio")) return "studio";
  if (lower.includes("دور") || lower.includes("floor")) return "floor";
  if (lower.includes("استراحة") || lower.includes("retreat")) return "retreat";
  if (lower.includes("مبنى") || lower.includes("عمارة") || lower.includes("building")) return "building";
  if (lower.includes("مجمع") || lower.includes("compound")) return "compound";
  if (lower.includes("مزرعة") || lower.includes("farm")) return "farm";
  if (lower.includes("مركز تجاري") || lower.includes("commercial center")) return "commercial_center";
  if (lower.includes("مصنع") || lower.includes("factory")) return "factory";
  if (lower.includes("محطة طاقة") || lower.includes("محطة كهرباء") || lower.includes("power plant")) return "power_plant";
  if (lower.includes("برج اتصالات") || lower.includes("telecom")) return "telecom_tower";
  if (lower.includes("محطة وقود") || lower.includes("gas station")) return "gas_station";
  if (lower.includes("مستشفى") || lower.includes("مركز صحي") || lower.includes("health") || lower.includes("hospital")) return "health_center";
  if (lower.includes("مدرسة") || lower.includes("school")) return "school";
  // REGA-specific types (28 total from Lookup V3.5)
  if (lower.includes("محل") || lower.includes("shop") || lower.includes("store")) return "commercial_center";
  if (lower.includes("مكتب") || lower.includes("office")) return "building";
  if (lower.includes("شاليه") || lower.includes("شالية") || lower.includes("chalet")) return "retreat";
  if (lower.includes("مستودع") || lower.includes("warehouse")) return "building";
  if (lower.includes("غرفة") || lower.includes("room")) return "room";
  if (lower.includes("برج") || lower.includes("tower")) return "building";
  if (lower.includes("معرض") || lower.includes("showroom")) return "commercial_center";
  if (lower.includes("كشك") || lower.includes("kiosk")) return "commercial_center";
  if (lower.includes("سينما") || lower.includes("cinema")) return "commercial_center";
  if (lower.includes("فندق") || lower.includes("hotel")) return "commercial_center";
  if (lower.includes("موقف") || lower.includes("parking")) return "commercial_center";
  if (lower.includes("ورشة") || lower.includes("workshop")) return "commercial_center";
  if (lower.includes("صراف") || lower.includes("atm")) return "commercial_center";
  if (lower.includes("محطة") || lower.includes("station")) return "gas_station";
  if (lower.includes("تجاري") || lower.includes("commercial")) return "commercial_center";
  if (lower.includes("صناعي") || lower.includes("industrial")) return "factory";
  if (lower.includes("مختلط") || lower.includes("mixed")) return "building";
  return "apartment";
}

/**
 * Maps REGA property type to unit_type enum values.
 */
export function mapRegaToUnitType(
  regaType: string
): string {
  if (!regaType || typeof regaType !== 'string') return "apartment";
  const lower = regaType.toLowerCase();
  if (lower.includes("أرض") || lower.includes("land")) return "land";
  if (lower.includes("فيلا") || lower.includes("villa")) return "villa";
  if (lower.includes("شقة") || lower.includes("apartment")) return "apartment";
  if (lower.includes("مكتب") || lower.includes("office")) return "office";
  if (lower.includes("محل") || lower.includes("shop") || lower.includes("store")) return "shop";
  if (lower.includes("مستودع") || lower.includes("warehouse")) return "warehouse";
  if (lower.includes("استوديو") || lower.includes("studio")) return "studio";
  if (lower.includes("دوبلكس") || lower.includes("duplex")) return "duplex";
  if (lower.includes("مبنى") || lower.includes("building")) return "building";
  return "apartment";
}

/**
 * Maps REGA advertisementType to listing_type.
 */
export function mapRegaToListingType(regaAdType: string): "rent" | "sale" {
  if (!regaAdType || typeof regaAdType !== 'string') return "rent";
  const lower = regaAdType.toLowerCase();
  if (lower.includes("بيع") || lower.includes("sell") || lower.includes("sale")) return "sale";
  return "rent";
}

/**
 * Maps REGA propertyUsages to Dallali property purpose (use-type).
 * REGA returns this as a string array (verified against prod data); legacy callers
 * may still pass a plain string, so we accept both. Falls back to deriving purpose
 * from the DB property_type when usages is empty/missing.
 */
export function mapRegaUsagesToPurpose(
  usages: string | string[] | undefined,
  dbPropertyType: string
): string {
  // Normalize to a single lowercased haystack so the same keyword scan works for both shapes.
  let haystack: string | null = null;
  if (Array.isArray(usages) && usages.length > 0) {
    haystack = usages.filter(u => typeof u === 'string').join(' ').toLowerCase();
  } else if (typeof usages === 'string' && usages.length > 0) {
    haystack = usages.toLowerCase();
  }
  if (haystack) {
    if (haystack.includes("سكني") || haystack.includes("residential")) return "residential";
    if (haystack.includes("تجاري") || haystack.includes("commercial")) return "commercial";
    if (haystack.includes("صناعي") || haystack.includes("industrial")) return "industrial";
    if (haystack.includes("زراعي") || haystack.includes("agricultural")) return "agricultural";
    if (haystack.includes("صحي") || haystack.includes("medical")) return "medical";
    if (haystack.includes("تعليمي") || haystack.includes("educational")) return "educational";
  }
  // Fallback: reverse-map from DB property_type (specific types)
  const typeMap: Record<string, string> = {
    // Old coarse types (legacy data)
    residential: "residential",
    commercial: "commercial",
    industrial: "industrial",
    mixed: "multi_purpose",
    // Specific types
    land: "agricultural",
    farm: "agricultural",
    floor: "residential",
    apartment: "residential",
    villa: "residential",
    studio: "residential",
    room: "residential",
    retreat: "residential",
    compound: "residential",
    building: "residential",
    commercial_center: "commercial",
    factory: "industrial",
    power_plant: "industrial",
    telecom_tower: "industrial",
    gas_station: "industrial",
    health_center: "medical",
    school: "educational",
  };
  return typeMap[dbPropertyType] || "residential";
}

/**
 * Stable classification of a REGA `propertyUtilities` label string into one of a known
 * set of categories, used by the listing page to pick an icon. Returns `'unknown'` when
 * none match — callers can fall back to a generic icon.
 *
 * Why this exists: the previous (lossy) implementation hardcoded a 6-item filter that
 * silently dropped REGA values outside the set. In particular it used `"ماء"` (singular)
 * as the water keyword while REGA actually returns `"مياه"` (plural) — so water never
 * rendered. It also dropped sewage / phone / flood-drainage categories entirely.
 */
export type RegaUtilityKind =
  | 'electricity'
  | 'water'
  | 'sewage'
  | 'fiber'
  | 'ac'
  | 'parking'
  | 'security'
  | 'phone'
  | 'unknown';

export function classifyRegaUtility(label: string): RegaUtilityKind {
  if (!label || typeof label !== 'string') return 'unknown';
  const lower = label.toLowerCase();
  if (lower.includes('electricity') || label.includes('كهرباء')) return 'electricity';
  // Cover both "ماء" (singular, our seed/wizard) and "مياه" (plural, REGA's actual response).
  if (lower.includes('water') || label.includes('ماء') || label.includes('مياه')) return 'water';
  if (lower.includes('sewage') || lower.includes('drainage') || label.includes('صرف') || label.includes('تصريف')) return 'sewage';
  if (lower.includes('fiber') || label.includes('ألياف')) return 'fiber';
  if (lower.includes('ac') || lower.includes('a/c') || label.includes('تكييف')) return 'ac';
  if (lower.includes('parking') || label.includes('مواقف')) return 'parking';
  if (lower.includes('security') || label.includes('حراسة')) return 'security';
  if (lower.includes('phone') || lower.includes('telephone') || label.includes('هاتف')) return 'phone';
  return 'unknown';
}

/**
 * Extracts a safe RegaLocation object from the response.
 */
function extractLocation(location: RegaLocation | string | undefined): RegaLocation | undefined {
  if (!location || typeof location === "string") return undefined;
  return location;
}

/**
 * Builds a curated RegaDisplayData from the full validate response.
 * Only includes fields needed for public display — no audit/request data.
 */
export function buildRegaDisplayData(rega: RegaValidateResponse): RegaDisplayData {
  return {
    adLicenseNumber: rega.adLicenseNumber,
    advertiserId: rega.advertiserId,
    advertiserName: rega.advertiserName,
    advertiserType: rega.advertiserType,
    advertisementType: rega.advertisementType,
    deedNumber: rega.deedNumber,
    // AC1 fix: was never copied, so brokerageContractNumber (FAL) was silently
    // absent from every public ad even though the field exists on the source.
    brokerageContractNumber: rega.brokerageContractNumber,
    titleDeedTypeName: rega.titleDeedTypeName,
    propertyType: rega.propertyType,
    propertyArea: rega.propertyArea,
    propertyPrice: rega.propertyPrice,
    propertyFace: rega.propertyFace,
    propertyAge: rega.propertyAge,
    propertyUsages: rega.propertyUsages,
    propertyUtilities: rega.propertyUtilities,
    numberOfRooms: rega.numberOfRooms,
    streetWidth: rega.streetWidth,
    planNumber: rega.planNumber,
    landNumber: rega.landNumber,
    isConstrained: rega.isConstrained,
    obligationsOnTheProperty: rega.obligationsOnTheProperty,
    guaranteesAndTheirDuration: rega.guaranteesAndTheirDuration,
    complianceWithTheSaudiBuildingCode: rega.complianceWithTheSaudiBuildingCode,
    adLicenseUrl: rega.adLicenseUrl,
    ownershipTransferFeeType: rega.ownershipTransferFeeType,
    responsibleEmployeeName: rega.responsibleEmployeeName,
    responsibleEmployeePhoneNumber: rega.responsibleEmployeePhoneNumber,
    phoneNumber: rega.phoneNumber,
    locationDescriptionOnMOJDeed: rega.locationDescriptionOnMOJDeed,
    location: extractLocation(rega.location),
    borders: rega.borders,
    creationDate: rega.creationDate,
    endDate: rega.endDate,
    // Additional fields visible on REGA's verification portal — copied for parity.
    brokerageAndMarketingLicenseNumber: rega.brokerageAndMarketingLicenseNumber,
    mainLandUseTypeName: rega.mainLandUseTypeName,
    redZoneTypeName: rega.redZoneTypeName,
    channels: rega.channels,
    adSource: rega.adSource,
    isHalted: rega.isHalted,
    isPawned: rega.isPawned,
    isTestment: rega.isTestment,
    notes: rega.notes,
    landTotalPrice: rega.landTotalPrice,
    landTotalAnnualRent: rega.landTotalAnnualRent,
    // Portal-API extras (validate API doesn't set these; pass through if present).
    rightsAndObligationsOnTheProperty: rega.rightsAndObligationsOnTheProperty,
    status: rega.status,
    statusCode: rega.statusCode,
    platformComplianceDto: rega.platformComplianceDto,
  };
}
